/**
 * RoleManagerDialog · v2.7.0 batch DO+
 * ====================================
 * Drop-in dialog for managing a driver/staff member's canonical roles[].
 * Backed by PUT /api/admin/drivers/{id}/roles. Handles the hierarchy guard
 * surfaced by the backend:
 *   • Operating Partner / Developer → no PIN needed
 *   • Dispatcher → must enter the OP/Dev PIN when granting privileged roles
 *   • Team Leader / Field Supervisor → cannot grant privileged roles at all
 *     (the UI hides those checkboxes)
 *   • Field Supervisor → cannot modify privileged accounts at all
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Lock } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Badge } from "../ui/badge";

import adminApi from "../../lib/adminApi";

const ROLE_CATALOG = [
  { value: "rider", label: "Rider", desc: "Books rides via /ride", privileged: false },
  { value: "driver", label: "Driver", desc: "Operates a taxi", privileged: false },
  { value: "team_leader", label: "Team Leader", desc: "Driver + rider mgmt; no privileged-account ops", privileged: false },
  { value: "field_supervisor", label: "Field Supervisor", desc: "Same as Team Leader; blocked from Dispatch/OP/Dev", privileged: true },
  { value: "dispatcher", label: "Dispatcher", desc: "Dispatch console (PIN required for role grants)", privileged: true },
  { value: "operating_partner", label: "Operating Partner", desc: "Ultimate business authority", privileged: true },
  { value: "developer", label: "Developer", desc: "Super-user; Developer area only", privileged: true },
  // v2.7.58+E42 · General Partner sits above every other role. This is
  // the supreme super-user — bypasses every permission gate across the
  // platform (backend get_current_admin sets is_super_root=True on login).
  { value: "general_partner", label: "General Partner", desc: "SUPREME super-user · bypasses every permission gate across the platform", privileged: true },
];

export default function RoleManagerDialog({ open, onOpenChange, driver, onSaved }) {
  const [selected, setSelected] = useState([]);
  const [opPin, setOpPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [callerRoles, setCallerRoles] = useState([]);
  const [callerLoaded, setCallerLoaded] = useState(false);

  // Reset state when driver changes
  useEffect(() => {
    if (!driver) return;
    const initial = driver.roles && driver.roles.length
      ? driver.roles
      : (driver.role ? [driver.role === "supervisor" ? "operating_partner" : driver.role] : ["driver"]);
    setSelected(initial);
    setOpPin("");
  }, [driver]);

  // Caller's roles for UI gating
  useEffect(() => {
    if (!open) return;
    setCallerLoaded(false);
    adminApi.get("/admin/me/roles")
      .then((r) => setCallerRoles(r.data?.roles || []))
      .catch(() => setCallerRoles([]))
      .finally(() => setCallerLoaded(true));
  }, [open]);

  const isSuper = useMemo(
    () => callerRoles.some((r) => ["operating_partner", "developer", "general_partner", "admin"].includes(r)),
    [callerRoles]
  );
  const isDispatcher = useMemo(
    () => callerRoles.includes("dispatcher") && !isSuper,
    [callerRoles, isSuper]
  );
  const isFieldSupervisor = useMemo(
    () => callerRoles.includes("field_supervisor"),
    [callerRoles]
  );

  const targetIsPrivileged = useMemo(() => {
    const cur = driver?.roles || [driver?.role];
    return (cur || []).some((r) => ["dispatcher", "operating_partner", "developer", "general_partner"].includes(r));
  }, [driver]);

  const fieldSupervisorBlocked = isFieldSupervisor && targetIsPrivileged && !isSuper;

  const newPrivileged = useMemo(
    () => selected.some((r) => ["dispatcher", "operating_partner", "developer", "general_partner"].includes(r)),
    [selected]
  );

  const showPinField = !isSuper && newPrivileged;

  const toggle = (value) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((r) => r !== value) : [...prev, value]
    );
  };

  const save = async () => {
    if (!selected.length) {
      toast.error("Select at least one role");
      return;
    }
    setBusy(true);
    try {
      const payload = { roles: selected };
      if (showPinField) payload.op_pin = opPin.trim();
      const { data } = await adminApi.put(`/admin/drivers/${driver.id}/roles`, payload);
      toast.success(`Roles updated · ${data.roles.join(", ").replace(/_/g, " ")}`);
      onSaved && onSaved(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save roles");
    } finally {
      setBusy(false);
    }
  };

  if (!driver) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-zinc-900 border-zinc-700 text-zinc-100 max-w-lg"
        data-testid="role-manager-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-amber-400"/>
            Manage Roles · {driver.name || driver.email}
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">
            Multi-role account. Toggle as many as needed — the highest-privilege
            role becomes the primary for legacy back-compat.
          </DialogDescription>
        </DialogHeader>

        {!callerLoaded && (
          <div className="py-6 text-center text-zinc-400 text-xs"><Loader2 className="h-4 w-4 inline mr-2 animate-spin"/>Loading…</div>
        )}

        {callerLoaded && fieldSupervisorBlocked && (
          <div className="rounded border border-red-500/40 bg-red-500/5 p-3 text-xs text-red-300" data-testid="role-mgr-blocked-field-supervisor">
            <Lock className="h-3.5 w-3.5 inline mr-1"/>
            Field Supervisors cannot modify Dispatch / Operating Partner / Developer accounts.
          </div>
        )}

        {callerLoaded && !fieldSupervisorBlocked && (
          <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
            {ROLE_CATALOG.map((r) => {
              const checked = selected.includes(r.value);
              // Hide roles a non-super caller cannot grant unless they ARE
              // dispatcher (then they CAN, with PIN).
              const canPick = isSuper || !r.privileged || isDispatcher;
              if (!canPick) return null;
              return (
                <label
                  key={r.value}
                  data-testid={`role-mgr-opt-${r.value}`}
                  className={`flex items-start gap-3 rounded border p-2.5 cursor-pointer transition ${
                    checked
                      ? "border-amber-400/50 bg-amber-400/5"
                      : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"
                  }`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(r.value)}
                    className="mt-1"
                    data-testid={`role-mgr-cb-${r.value}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-sm font-semibold">
                      {r.label}
                      {r.privileged && (
                        <Badge variant="outline" className="border-amber-400/40 text-amber-300 text-[9px] py-0">
                          PRIVILEGED
                        </Badge>
                      )}
                    </div>
                    <div className="text-[10px] text-zinc-500 leading-snug mt-0.5">{r.desc}</div>
                  </div>
                </label>
              );
            })}
          </div>
        )}

        {showPinField && !fieldSupervisorBlocked && (
          <div className="rounded border border-amber-400/40 bg-amber-400/5 p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-bold">
              Role-Assignment PIN required
            </div>
            <Label className="text-xs text-amber-100">
              Enter an Operating Partner / Developer PIN (or the per-role PIN
              configured in Developer → Role PINs).
            </Label>
            <Input
              value={opPin}
              onChange={(e) => setOpPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
              maxLength={8}
              data-testid="role-mgr-pin"
              autoFocus
              type="password"
              inputMode="numeric"
              className="bg-zinc-950 border-amber-400/40 text-amber-100 text-center tracking-[0.6em] text-lg"
              placeholder="••••"
            />
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-zinc-400 hover:text-zinc-200"
            data-testid="role-mgr-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={busy || fieldSupervisorBlocked || (showPinField && opPin.length < 4)}
            className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold"
            data-testid="role-mgr-save"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin"/> : null}
            Save Roles
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
